import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiCpu,
  FiSend,
  FiRefreshCw,
  FiCheckCircle,
  FiAlertCircle,
  FiChevronDown,
  FiChevronUp,
  FiDollarSign,
  FiZap,
  FiCode,
  FiEye,
} from 'react-icons/fi';
import type { AIUsage, AssistantPhase, IntentAnalysis, GeneratedCode, ValidationResult } from '../../hooks/useStrategyAssistant';
import CodeHighlighter from '../ai/CodeHighlighter';

interface NotebookCell {
  type: 'code' | 'markdown';
  content: string;
}

interface AIAssistantCellProps {
  onInsertCode?: (code: string, type?: 'code' | 'markdown') => void;
  className?: string;
  /** Start collapsed (e.g., when a notebook auto-opens from the launch page) */
  defaultExpanded?: boolean;
  // Hook state passed from parent
  phase: AssistantPhase;
  analysis: IntentAnalysis | null;
  answers: Record<string, string>;
  generatedCode: GeneratedCode | null;
  validationResult: ValidationResult | null;
  error: string | null;
  isLoading: boolean;
  sessionUsage: AIUsage;
  // Hook actions passed from parent
  analyzeIntent: (intent: string) => Promise<void>;
  submitAnswer: (questionId: string, answer: string) => void;
  finishQuestioning: () => void;
  validateCode: (code: string) => Promise<void>;
  fixCode: () => Promise<void>;
  prepareForNotebook: () => Promise<NotebookCell[] | null>;
  reset: () => void;
}

/**
 * AIAssistantCell - AI assistant that sits above Jupyter cells.
 *
 * Designed to look like a notebook cell with:
 * - Collapsible header (chevron toggle)
 * - Expanded state with full Socratic flow
 *
 * Receives hook state/actions from parent to share state with GPU metrics.
 */
export default function AIAssistantCell({
  onInsertCode,
  className = '',
  defaultExpanded = true,
  phase,
  analysis,
  answers,
  generatedCode,
  validationResult,
  error,
  isLoading,
  sessionUsage,
  analyzeIntent,
  submitAnswer,
  finishQuestioning,
  validateCode,
  fixCode,
  prepareForNotebook,
  reset,
}: AIAssistantCellProps) {

  const [intentInput, setIntentInput] = useState('');
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [validationMsgIndex, setValidationMsgIndex] = useState(0);
  const [isInserting, setIsInserting] = useState(false);
  const [insertProgress, setInsertProgress] = useState<{ current: number; total: number; label: string } | null>(null);

  // Rotating validation messages to keep users engaged
  const validationMessages = [
    { text: 'Checking for look-ahead bias...', icon: '🔍' },
    { text: 'Analyzing risk management logic...', icon: '⚖️' },
    { text: 'Reviewing entry/exit conditions...', icon: '📊' },
    { text: 'Scanning for data leakage...', icon: '🛡️' },
    { text: 'Evaluating edge cases...', icon: '🧪' },
    { text: 'Checking position sizing logic...', icon: '📐' },
    { text: 'Reviewing error handling...', icon: '🔧' },
    { text: 'Almost done...', icon: '✨' },
  ];

  // Rotate validation messages every 2 seconds while validating
  useEffect(() => {
    if (phase === 'validating') {
      setValidationMsgIndex(0);
      const interval = setInterval(() => {
        setValidationMsgIndex(i => (i + 1) % validationMessages.length);
      }, 2000);
      return () => clearInterval(interval);
    }
  }, [phase, validationMessages.length]);

  const handleSubmitIntent = () => {
    if (intentInput.trim().length >= 10) {
      analyzeIntent(intentInput.trim());
    }
  };

  const handleValidate = () => {
    if (generatedCode) {
      validateCode(generatedCode.code);
    }
  };

  const handleInsertCode = async () => {
    if (!generatedCode || !onInsertCode) return;

    setIsInserting(true);
    setInsertProgress({ current: 0, total: 0, label: 'Preparing notebook cells...' });

    try {
      // Get prepared cells with summary and enhanced code
      const cells = await prepareForNotebook();

      if (!cells || cells.length === 0) {
        // Fallback: just insert the raw code
        onInsertCode(generatedCode.code, 'code');
        setIsInserting(false);
        setInsertProgress(null);
        return;
      }

      setInsertProgress({ current: 0, total: cells.length, label: 'Inserting cells...' });

      // Insert cells one by one with delay for visual feedback
      for (let i = 0; i < cells.length; i++) {
        const cell = cells[i];
        const cellLabel = cell.type === 'markdown' ? 'Adding summary...' :
          i === 1 ? 'Adding imports...' :
          `Adding code block ${i}...`;

        setInsertProgress({ current: i + 1, total: cells.length, label: cellLabel });

        onInsertCode(cell.content, cell.type);

        // Small delay between insertions for visual effect
        if (i < cells.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 300));
        }
      }

      setInsertProgress({ current: cells.length, total: cells.length, label: 'Done!' });

      // Clear after a moment
      setTimeout(() => {
        setIsInserting(false);
        setInsertProgress(null);
      }, 1000);

    } catch (err) {
      console.error('Failed to insert code:', err);
      // Fallback: just insert the raw code
      onInsertCode(generatedCode.code, 'code');
      setIsInserting(false);
      setInsertProgress(null);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      handleSubmitIntent();
    }
  };

  return (
    <div className={`mx-4 mb-4 ${className}`}>
      <div className="bg-gray-900/80 border border-gray-700/50 rounded-lg overflow-hidden backdrop-blur-sm">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-700/50 bg-gray-800/50">
          <div className="flex items-center gap-3">
            <div className="p-1.5 bg-purple-900/50 rounded">
              <FiCpu className="w-4 h-4 text-purple-400" />
            </div>
            <div>
              <h3 className="text-sm font-medium text-white">AI Strategy Assistant</h3>
              <p className="text-xs text-gray-500">
                {phase === 'idle' && 'Describe your strategy idea'}
                {phase === 'analyzing' && 'Analyzing...'}
                {phase === 'questioning' && 'Answer questions'}
                {phase === 'generating' && 'Generating code...'}
                {phase === 'validating' && 'Validating...'}
                {phase === 'complete' && 'Strategy ready'}
                {phase === 'error' && 'Error occurred'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {sessionUsage.costUsd > 0 && (
              <div className="flex items-center gap-1 text-xs text-gray-500 mr-2">
                <FiDollarSign className="w-3 h-3" />
                <span>${sessionUsage.costUsd.toFixed(4)}</span>
              </div>
            )}

            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-1.5 text-gray-400 hover:text-white rounded transition-colors"
              title={isExpanded ? 'Collapse' : 'Expand'}
            >
              {isExpanded ? <FiChevronUp className="w-4 h-4" /> : <FiChevronDown className="w-4 h-4" />}
            </button>

          </div>
        </div>

        {/* Content */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="p-4 space-y-4">
                {/* Phase: Idle - Intent input */}
                {phase === 'idle' && (
                  <div className="space-y-3">
                    <textarea
                      value={intentInput}
                      onChange={(e) => setIntentInput(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder="Describe your trading strategy... (e.g., 'Buy when RSI drops below 30 and sell when it goes above 70')"
                      className="w-full h-20 px-3 py-2 bg-gray-800/50 border border-gray-700/50 rounded text-sm text-white placeholder-gray-500 focus:border-purple-500/50 focus:outline-none resize-none font-mono"
                    />
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">
                        {intentInput.length < 10 ? `${10 - intentInput.length} more characters needed` : 'Cmd/Ctrl + Enter to submit'}
                      </span>
                      <button
                        onClick={handleSubmitIntent}
                        disabled={intentInput.trim().length < 10}
                        className="flex items-center gap-2 px-4 py-1.5 bg-purple-600 text-white rounded text-sm font-medium hover:bg-purple-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <FiSend className="w-3.5 h-3.5" />
                        Analyze
                      </button>
                    </div>
                  </div>
                )}

                {/* Phase: Analyzing */}
                {phase === 'analyzing' && (
                  <div className="flex items-center justify-center py-6">
                    <FiRefreshCw className="w-5 h-5 text-purple-400 animate-spin mr-3" />
                    <span className="text-gray-300">Analyzing your strategy intent...</span>
                  </div>
                )}

                {/* Phase: Questioning */}
                {phase === 'questioning' && analysis && (
                  <div className="space-y-2 text-left">
                    <p className="text-xs text-gray-500 uppercase tracking-wide">
                      Clarify your strategy
                    </p>

                    {/* Questions on same line as options, left-aligned */}
                    <div className="space-y-1.5">
                      {analysis.clarifyingQuestions.map((q) => (
                        <div
                          key={q.id}
                          className="flex items-center gap-3 py-1.5 px-2 bg-gray-800/30 rounded border border-gray-700/20"
                        >
                          {/* Question text */}
                          <p className="text-sm text-gray-300 whitespace-nowrap">{q.question}</p>

                          {/* Options - multi-select with toggle */}
                          <div className="flex flex-wrap gap-1.5">
                            {q.options.map((opt) => {
                              const currentAnswers = answers[q.id]?.split(',').filter(Boolean) || [];
                              const isSelected = currentAnswers.includes(opt.value);

                              const handleToggle = () => {
                                let newAnswers: string[];
                                if (isSelected) {
                                  newAnswers = currentAnswers.filter(a => a !== opt.value);
                                } else {
                                  newAnswers = [...currentAnswers, opt.value];
                                }
                                submitAnswer(q.id, newAnswers.join(','));
                              };

                              return (
                                <button
                                  key={opt.value}
                                  onClick={handleToggle}
                                  title={opt.description}
                                  className={`px-2 py-0.5 text-xs rounded-full transition-all ${
                                    isSelected
                                      ? 'bg-purple-600 text-white ring-1 ring-purple-400'
                                      : 'bg-gray-700/50 text-gray-400 hover:bg-gray-600/80 hover:text-white'
                                  }`}
                                >
                                  {opt.label}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Generate button */}
                    <button
                      onClick={finishQuestioning}
                      disabled={isLoading}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 text-white rounded text-sm font-medium hover:bg-purple-500 transition-colors disabled:opacity-50 mt-2"
                    >
                      <FiZap className="w-3.5 h-3.5" />
                      Generate Code
                    </button>
                  </div>
                )}

                {/* Phase: Generating */}
                {phase === 'generating' && (
                  <div className="flex items-center justify-center py-6">
                    <FiZap className="w-5 h-5 text-purple-400 animate-pulse mr-3" />
                    <span className="text-gray-300">Generating your strategy code...</span>
                  </div>
                )}

                {/* Phase: Validating - show code with progress overlay */}
                {phase === 'validating' && generatedCode && (
                  <div className="space-y-3">
                    {/* Validation progress indicator */}
                    <div className="flex items-center gap-3 p-3 bg-blue-900/20 border border-blue-700/30 rounded-lg">
                      <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-blue-900/40 rounded-full">
                        <span className="text-lg animate-pulse">{validationMessages[validationMsgIndex].icon}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-blue-300 font-medium">Deep validation in progress</p>
                        <p className="text-xs text-blue-400/80 truncate">
                          {validationMessages[validationMsgIndex].text}
                        </p>
                      </div>
                      <FiRefreshCw className="w-4 h-4 text-blue-400 animate-spin flex-shrink-0" />
                    </div>

                    {/* Show code preview (dimmed) while validating */}
                    <div className="opacity-60 border border-purple-900/30 rounded-lg overflow-hidden bg-gray-950/50 max-h-60 overflow-y-auto">
                      <CodeHighlighter
                        code={generatedCode.code}
                        highlightedLines={generatedCode.highlightedLines || {}}
                        showLineNumbers={true}
                      />
                    </div>
                  </div>
                )}

                {/* Phase: Complete */}
                {phase === 'complete' && generatedCode && (
                  <div className="space-y-4">
                    {/* Preview indicator */}
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <FiEye className="w-3 h-3" />
                      <span>Preview — Insert into notebook to execute</span>
                    </div>

                    {/* Syntax-highlighted code preview */}
                    <div className="border border-purple-900/30 rounded-lg overflow-hidden bg-gray-950/50 max-h-80 overflow-y-auto">
                      <CodeHighlighter
                        code={generatedCode.code}
                        highlightedLines={generatedCode.highlightedLines || {}}
                        warnings={generatedCode.warnings}
                        suggestions={generatedCode.suggestions}
                        showLineNumbers={true}
                      />
                    </div>

                    {/* Validation result */}
                    {validationResult && (
                      <div className={`p-3 rounded border ${
                        validationResult.isValid
                          ? 'bg-green-900/20 border-green-700/30'
                          : 'bg-red-900/20 border-red-700/30'
                      }`}>
                        <div className="flex items-start gap-2">
                          {validationResult.isValid ? (
                            <FiCheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                          ) : (
                            <FiAlertCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                          )}
                          <div className="flex-1 text-xs">
                            <span className={validationResult.isValid ? 'text-green-400' : 'text-red-400'}>
                              {validationResult.isValid ? 'Validated' : 'Issues found'}
                            </span>
                            {validationResult.errors.length > 0 && (
                              <ul className="mt-1 text-red-300 list-disc list-inside">
                                {validationResult.errors.map((e, i) => (
                                  <li key={i}>{e}</li>
                                ))}
                              </ul>
                            )}
                          </div>
                        </div>
                        {/* Fix Issues button when validation fails */}
                        {!validationResult.isValid && (
                          <button
                            onClick={fixCode}
                            disabled={isLoading}
                            className="mt-3 flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 text-white rounded text-sm font-medium hover:bg-amber-500 transition-colors disabled:opacity-50"
                          >
                            <FiZap className="w-3.5 h-3.5" />
                            Apply Suggested Fixes
                          </button>
                        )}
                      </div>
                    )}

                    {/* Insertion progress */}
                    {isInserting && insertProgress && (
                      <div className="flex items-center gap-3 p-3 bg-purple-900/20 border border-purple-700/30 rounded-lg">
                        <FiRefreshCw className="w-4 h-4 text-purple-400 animate-spin flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-purple-300 font-medium">{insertProgress.label}</p>
                          {insertProgress.total > 0 && (
                            <div className="mt-1.5 h-1.5 bg-gray-700 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-purple-500 transition-all duration-300"
                                style={{ width: `${(insertProgress.current / insertProgress.total) * 100}%` }}
                              />
                            </div>
                          )}
                        </div>
                        <span className="text-xs text-purple-400">
                          {insertProgress.total > 0 ? `${insertProgress.current}/${insertProgress.total}` : ''}
                        </span>
                      </div>
                    )}

                    {/* Actions */}
                    {!isInserting && (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={handleInsertCode}
                          disabled={isLoading}
                          className="flex items-center gap-2 px-4 py-1.5 bg-purple-600 text-white rounded text-sm font-medium hover:bg-purple-500 transition-colors disabled:opacity-50"
                        >
                          <FiCode className="w-3.5 h-3.5" />
                          Insert into Notebook
                        </button>
                        <button
                          onClick={handleValidate}
                          disabled={isLoading}
                          className="flex items-center gap-2 px-3 py-1.5 bg-gray-700 text-gray-200 rounded text-sm hover:bg-gray-600 transition-colors disabled:opacity-50"
                        >
                          <FiCheckCircle className="w-3.5 h-3.5" />
                          Validate
                        </button>
                        <button
                          onClick={reset}
                          className="flex items-center gap-2 px-3 py-1.5 text-gray-400 hover:text-white transition-colors text-sm"
                        >
                          <FiRefreshCw className="w-3.5 h-3.5" />
                          Start Over
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Phase: Error */}
                {phase === 'error' && (
                  <div className="space-y-3">
                    <div className="flex items-start gap-2 p-3 bg-red-900/20 border border-red-700/30 rounded">
                      <FiAlertCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-red-300 font-medium">Something went wrong</p>
                        <p className="text-xs text-red-400 mt-1">{error}</p>
                      </div>
                    </div>
                    <button
                      onClick={reset}
                      className="flex items-center gap-2 px-3 py-1.5 bg-gray-700 text-gray-200 rounded text-sm hover:bg-gray-600 transition-colors"
                    >
                      <FiRefreshCw className="w-3.5 h-3.5" />
                      Try Again
                    </button>
                  </div>
                )}

                {/* Token usage footer */}
                {sessionUsage.inputTokens > 0 && (
                  <div className="pt-3 border-t border-gray-700/50 flex items-center justify-between text-xs text-gray-500">
                    <span>
                      {sessionUsage.inputTokens.toLocaleString()} in / {sessionUsage.outputTokens.toLocaleString()} out tokens
                    </span>
                    <span>Session: ${sessionUsage.costUsd.toFixed(4)}</span>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
